import React, { useCallback } from 'react';

import { Delete } from '@mui/icons-material';
import { Rectangle } from '@mui/icons-material';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { getIn, useFormikContext } from 'formik';
import {
  AlertConfiguration,
  IndicatorAlertCriticalityEnum,
} from 'src/types/indicator-alert-configuration';

import { useAlertContext } from '../../../context/AlertContextProvider';
import { AlertValidationSchema } from '../../../validation/alertValidation';

type AlertInputRowProps = {
  indicatorIndex: number;
  alertIndex: number;
  alert: AlertConfiguration;
  indicatorKey: string;
  onUpdateAlert: (
    indicatorKey: string,
    alertId: string,
    field: string,
    value: any,
  ) => void;
  onRemoveAlert: (
    indicatorKey: string,
    alertId: string,
    id: number | null,
  ) => void;
};

export function AlertInputRow({
  indicatorIndex,
  alertIndex,
  alert,
  indicatorKey,
  onUpdateAlert,
  onRemoveAlert,
}: AlertInputRowProps) {
  const {
    values,
    setFieldValue,
    errors,
    touched,
    setFieldTouched,
    validateField,
  } = useFormikContext<AlertValidationSchema>();

  const { useAlertDefaultData } = useAlertContext();

  const handleFieldChange = (field: string, value: any) => {
    const fieldPath = `indicatorsWithAlerts.${indicatorIndex}.alerts.${alertIndex}.${field}`;
    setFieldValue(fieldPath, value);
    setFieldTouched(fieldPath, true);

    onUpdateAlert(indicatorKey, alert.internalId, field, value);
  };

  const handleRemove = () => {
    onRemoveAlert(indicatorKey, alert.internalId, alert.id);
  };

  const operatorField = `indicatorsWithAlerts.${indicatorIndex}.alerts.${alertIndex}.operator`;
  const operatorError =
    getIn(touched, operatorField) && getIn(errors, operatorField);
  const startValueField = `indicatorsWithAlerts.${indicatorIndex}.alerts.${alertIndex}.startValue`;
  const startValueError =
    getIn(touched, startValueField) && getIn(errors, startValueField);

  const endValueField = `indicatorsWithAlerts.${indicatorIndex}.alerts.${alertIndex}.endValue`;
  const endValueError =
    getIn(touched, endValueField) && getIn(errors, endValueField);
  const criticalityField = `indicatorsWithAlerts.${indicatorIndex}.alerts.${alertIndex}.criticality`;
  const criticalityError =
    getIn(touched, criticalityField) && getIn(errors, criticalityField);
  const isActiveField = `indicatorsWithAlerts.${indicatorIndex}.alerts.${alertIndex}.isActive`;
  const isActiveError =
    getIn(touched, isActiveField) && getIn(errors, isActiveField);
  const emailField = `indicatorsWithAlerts.${indicatorIndex}.alerts.${alertIndex}.email`;
  const emailError = getIn(touched, emailField) && getIn(errors, emailField);

  const handleOperatorChange = useCallback(
    (value: string) => {
      setTimeout(() => {
        setFieldValue(operatorField, value);
        setFieldTouched(operatorField, true);

        if (value !== 'between' && endValueField !== null) {
          setFieldValue(endValueField, 0);
          setFieldTouched(endValueField, true);
        }
      }, 0);

      onUpdateAlert(indicatorKey, alert.internalId, 'operator', value);
      if (value !== 'between' && endValueField !== null) {
        onUpdateAlert(indicatorKey, alert.internalId, 'endValue', null);
      }
    },
    [
      onUpdateAlert,
      setFieldValue,
      setFieldTouched,
      operatorField,
      indicatorKey,
      alert.internalId,
      endValueField,
    ],
  );

  const handleStartValueChange = useCallback(
    (value: number) => {
      setFieldValue(startValueField, value);
      setFieldTouched(startValueField, true);

      validateField(startValueField);
      onUpdateAlert(indicatorKey, alert.internalId, 'startValue', value);
    },
    [
      onUpdateAlert,
      setFieldValue,
      setFieldTouched,
      startValueField,
      validateField,
    ],
  );

  const handleEndValueChange = useCallback(
    (value: number) => {
      setFieldValue(endValueField, value);
      setFieldTouched(endValueField, true);
      validateField(endValueField);
      onUpdateAlert(indicatorKey, alert.internalId, 'endValue', value);
    },
    [
      onUpdateAlert,
      setFieldValue,
      setFieldTouched,
      endValueField,
      validateField,
    ],
  );

  const handleCriticalityChange = useCallback(
    (value: string) => {
      setFieldValue(criticalityField, value);
      setFieldTouched(criticalityField, true);
      onUpdateAlert(indicatorKey, alert.internalId, 'criticality', value);
    },
    [onUpdateAlert, setFieldValue, setFieldTouched, criticalityField],
  );

  const handleIsActiveChange = useCallback(
    (value: boolean) => {
      setFieldValue(isActiveField, value);
      setFieldTouched(isActiveField, true);
      onUpdateAlert(indicatorKey, alert.internalId, 'isActive', value);
    },
    [onUpdateAlert, setFieldValue, setFieldTouched, isActiveField],
  );

  const handleEmailChange = useCallback(
    (value: boolean) => {
      setFieldValue(emailField, value);
      setFieldTouched(emailField, true);
      onUpdateAlert(indicatorKey, alert.internalId, 'email', value);
    },
    [onUpdateAlert, setFieldValue, setFieldTouched, emailField],
  );

  return (
    <Box
      component="fieldset"
      disabled={useAlertDefaultData}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
        '&:last-of-type hr': {
          display: 'none',
        },
      }}>
      <Box
        display="flex"
        gap={2}
        alignItems="flex-start"
        mb={1}
        flexDirection={{ xs: 'column', md: 'row' }}>
        <FormControl fullWidth size="small">
          <InputLabel id="operator-label">Operador</InputLabel>
          <Select
            name={operatorField}
            labelId="operator-label"
            label="Operador"
            disabled={useAlertDefaultData}
            value={
              values.indicatorsWithAlerts?.[indicatorIndex]?.alerts?.[
                alertIndex
              ]?.operator || ''
            }
            onChange={(e) => handleOperatorChange(e.target.value)}
            displayEmpty
            variant="outlined">
            <MenuItem value="gt">Acima de</MenuItem>
            <MenuItem value="lt">Abaixo de</MenuItem>
            <MenuItem value="gte">Maior ou igual a</MenuItem>
            <MenuItem value="lte">Menor ou igual a</MenuItem>
            <MenuItem value="between">Entre</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <TextField
            label="Valor Inicial"
            fullWidth
            size="small"
            name={startValueField}
            disabled={useAlertDefaultData}
            value={
              values.indicatorsWithAlerts?.[indicatorIndex]?.alerts?.[
                alertIndex
              ]?.startValue || 0
            }
            onBlur={(e) => {
              handleStartValueChange(parseFloat(e.target.value) || 0);
              validateField(startValueField);
            }}
            onChange={(e) => {
              handleStartValueChange(parseFloat(e.target.value) || 0);
            }}
            type="number"
            InputProps={{
              startAdornment:
                alert.tabType === 'amount' ? (
                  <InputAdornment position="start">R$</InputAdornment>
                ) : null,
              endAdornment:
                alert.tabType === 'percentage' ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : null,
            }}
            inputProps={{
              step: '1.00',
            }}
            error={!!startValueError}
            helperText={startValueError || ''}
          />
        </FormControl>
        <FormControl fullWidth size="small">
          <TextField
            label="Valor Final"
            name={endValueField}
            fullWidth
            type="number"
            size="small"
            value={
              values.indicatorsWithAlerts?.[indicatorIndex]?.alerts?.[
                alertIndex
              ]?.endValue || 0
            }
            onBlur={(e) => {
              handleEndValueChange(parseFloat(e.target.value) || 0);
              validateField(endValueField);
            }}
            onChange={(e) =>
              handleEndValueChange(parseFloat(e.target.value) || 0)
            }
            disabled={
              useAlertDefaultData ||
              values.indicatorsWithAlerts?.[indicatorIndex]?.alerts?.[
                alertIndex
              ]?.operator !== 'between'
            }
            InputProps={{
              startAdornment:
                alert.tabType === 'amount' ? (
                  <InputAdornment position="start">R$</InputAdornment>
                ) : null,
              endAdornment:
                alert.tabType === 'percentage' ? (
                  <InputAdornment position="end">%</InputAdornment>
                ) : null,
            }}
            inputProps={{
              step: '1.00',
            }}
            error={!!endValueError}
            helperText={endValueError || ''}
          />
        </FormControl>
        <FormControl fullWidth size="small" error={!!criticalityError}>
          <InputLabel id="criticality-label">Criticidade</InputLabel>
          <Select
            name={criticalityField}
            labelId="criticality-label"
            label="Criticidade"
            disabled={useAlertDefaultData}
            value={
              values.indicatorsWithAlerts?.[indicatorIndex]?.alerts?.[
                alertIndex
              ]?.criticality || IndicatorAlertCriticalityEnum.ALERT
            }
            onChange={(e) => handleCriticalityChange(e.target.value)}
            displayEmpty
            variant="outlined">
            <MenuItem value={IndicatorAlertCriticalityEnum.ALERT}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Rectangle sx={{ color: 'gold', width: 16, height: 16 }} />
                <Typography variant="body2">Alerta</Typography>
              </Box>
            </MenuItem>
            <MenuItem value={IndicatorAlertCriticalityEnum.CRITICAL}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Rectangle sx={{ color: 'red', width: 16, height: 16 }} />
                <Typography variant="body2">Crítico</Typography>
              </Box>
            </MenuItem>
          </Select>
          {criticalityError && (
            <FormHelperText>{criticalityError}</FormHelperText>
          )}
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              name={isActiveField}
              disabled={useAlertDefaultData}
              checked={
                values.indicatorsWithAlerts?.[indicatorIndex]?.alerts?.[
                  alertIndex
                ]?.isActive || false
              }
              onChange={(e) => handleIsActiveChange(e.target.checked)}
              size="small"
            />
          }
          labelPlacement="top"
          label="Ativo"
          slotProps={{
            typography: {
              fontSize: '12px',
            },
          }}
        />
        <FormControlLabel
          control={
            <Switch
              name={emailField}
              disabled={useAlertDefaultData}
              checked={
                values.indicatorsWithAlerts?.[indicatorIndex]?.alerts?.[
                  alertIndex
                ]?.email || false
              }
              onChange={(e) => handleEmailChange(e.target.checked)}
              size="small"
            />
          }
          labelPlacement="top"
          label="Email"
          slotProps={{
            typography: {
              fontSize: '12px',
            },
          }}
        />
        <IconButton
          color="error"
          size="small"
          onClick={handleRemove}
          disabled={useAlertDefaultData}
          title="Remover alerta">
          <Delete />
        </IconButton>
      </Box>
      <Divider
        sx={{ width: '100%', mb: 1.5, mt: 0.5, borderStyle: 'dashed' }}
      />
    </Box>
  );
}
